import * as React from "react";
import { observer } from "mobx-react-lite";
import { TeamOutlined } from "@ant-design/icons";
import { Input, Select, Form, Collapse, Checkbox, Tooltip } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import TaskStatusesVisualStore from "../stores/TaskStatusesVisualStore";
import { useForm } from "antd/lib/form/Form";
import { Utils } from "src/modules/common/misc/Utils";
import AppModal from "../../common/components/AppModal";

type Props = {
    store: TaskStatusesVisualStore;
};
const FormItem = Form.Item;

const TaskStatusEditDialog: React.FC<Props> = ({ store }) => {
    const [form] = useForm();
    const [activeCollapseKey, setActiveCollapseKey] = React.useState<string[]>(
        [],
    );

    React.useEffect(() => {
        form.resetFields();
        setActiveCollapseKey([]);
    }, [store.statusEditDialogVisible]);

    if (!store.selectedTaskStatus) {
        return null;
    }

    const title = store.selectedTaskStatus.isSubtaskStatus
        ? "Edit subtask status"
        : "Edit task status";

    const handleSubmit = async () => {
        form.validateFields()
            .then((values) => {
                store.selectUsers(values.accessUserIds ?? []);
                store.updateTaskStatus(values.name, values.code);
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const handleAccessUsersChange = (selectedUserIds: string[]) => {
        if (selectedUserIds.length > 0) {
            form.setFieldValue("noRestrictions", false);
        }
    };

    const handleNoRestrictionsChange = (event: CheckboxChangeEvent) => {
        if (event.target.checked) {
            form.setFieldValue("accessUserIds", []);
            store.selectUsers([]);
        }
    };

    const handleCancel = () => {
        store.selectStatus(undefined);
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const statusNameValidator = (rule: any, value: any, callback: any) => {
        const statusesToValidate = store!.taskStatuses
            .filter(
                (s) =>
                    s.isSubtaskStatus ===
                    store.selectedTaskStatus?.isSubtaskStatus,
            )
            .map((t) => t.name.toLowerCase());
        if (
            value &&
            statusesToValidate.includes(value.toLowerCase()) &&
            store.selectedTaskStatus?.name.toLowerCase() !== value.toLowerCase()
        ) {
            callback("Status with the same name already exists");
        } else {
            callback();
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const statusCodeValidator = (rule: any, value: any, callback: any) => {
        const statusesToValidate = store!.taskStatuses
            .filter(
                (s) =>
                    s.isSubtaskStatus ===
                    store.selectedTaskStatus?.isSubtaskStatus,
            )
            .map((t) => t.code?.toLowerCase());
        if (
            value &&
            statusesToValidate.includes(value.toLowerCase()) &&
            store.selectedTaskStatus?.code?.toLowerCase() !==
                value.toLowerCase()
        ) {
            callback("Status with the same code already exists");
        } else {
            callback();
        }
    };

    return (
        <AppModal
            data-id-type="modal-window"
            data-id-name="edit-status"
            className="alpha-portal-dialog"
            title={title}
            open={store.statusEditDialogVisible}
            closable={false}
            centered
            cancelButtonTitle="Cancel"
            onCancelClick={handleCancel}
            cancelButtonProps={{
                "data-id": "button-edit-status-cancel",
            }}
            submitButtonTitle="Save"
            onSubmitClick={handleSubmit}
            submitButtonProps={{
                "data-id": "button-edit-status-save",
                className: "dark",
            }}
            width={520}
        >
            <Form
                layout="vertical"
                data-id="create-task-type-dialog-form"
                form={form}
                className="alpha-form"
                initialValues={{
                    accessUserIds: store.currentStatusUsers
                        ? store.currentStatusUsers.map((u) => u.id)
                        : [],
                    name: store.selectedTaskStatus.name,
                    code: store.selectedTaskStatus.code,
                    noRestrictions: store.currentStatusUsers.length === 0,
                }}
            >
                <div className="app-modal__form-stack">
                    <FormItem
                        label={<span className="dialog-field-label">Name</span>}
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: "Name is required",
                                whitespace: true,
                            },
                            { validator: statusNameValidator },
                        ]}
                    >
                        <Input data-id="input-Name" />
                    </FormItem>
                    <FormItem
                        label={<span className="dialog-field-label">Code</span>}
                        name="code"
                        rules={[{ validator: statusCodeValidator }]}
                    >
                        <Input data-id="input-code" />
                    </FormItem>
                    <Collapse
                        activeKey={activeCollapseKey}
                        bordered={false}
                        className="alpha-task-section-collapse"
                        expandIconPosition="end"
                        onChange={(keys) =>
                            setActiveCollapseKey(keys as string[])
                        }
                        items={[
                            {
                                key: "access",
                                collapsible: "disabled",
                                label: (
                                    <Tooltip
                                        title="Under construction"
                                        placement="bottom"
                                    >
                                        <div
                                            style={{
                                                alignItems: "center",
                                                display: "flex",
                                                gap: 14,
                                                paddingRight: 12,
                                            }}
                                        >
                                            <TeamOutlined
                                                style={{
                                                    color: "#475467",
                                                    fontSize: 16,
                                                }}
                                            />
                                            <div>
                                                <div
                                                    style={{
                                                        color: "#101828",
                                                        fontSize: 16,
                                                        fontWeight: 500,
                                                        lineHeight: "24px",
                                                    }}
                                                >
                                                    Access in this status
                                                    (optional)
                                                </div>
                                                <div
                                                    style={{
                                                        color: "#667085",
                                                        fontSize: 13,
                                                        lineHeight: "20px",
                                                        marginTop: 2,
                                                    }}
                                                >
                                                    Define which users can see
                                                    and interact with tasks in
                                                    this status.
                                                </div>
                                            </div>
                                        </div>
                                    </Tooltip>
                                ),
                                children: (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 12,
                                        }}
                                    >
                                        <div
                                            style={{
                                                color: "#475467",
                                                fontSize: 14,
                                            }}
                                        >
                                            Users and roles
                                        </div>
                                        <FormItem
                                            name="accessUserIds"
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Select
                                                mode="multiple"
                                                filterOption={
                                                    Utils.filterOption
                                                }
                                                getPopupContainer={(trigger) =>
                                                    trigger.parentElement ||
                                                    document.body
                                                }
                                                onChange={
                                                    handleAccessUsersChange
                                                }
                                                optionFilterProp="label"
                                                showSearch
                                                placeholder="Select users"
                                                options={
                                                    store.usersInProject
                                                        ? store.usersInProject.map(
                                                              (s) => ({
                                                                  label: s.userName,
                                                                  value: s.id,
                                                              }),
                                                          )
                                                        : []
                                                }
                                            />
                                        </FormItem>
                                        <div
                                            style={{
                                                color: "#667085",
                                            }}
                                        >
                                            Only selected users can see and
                                            interact with tasks while they are
                                            in this status.
                                        </div>
                                        <FormItem
                                            name="noRestrictions"
                                            valuePropName="checked"
                                            style={{ marginBottom: 0 }}
                                        >
                                            <Checkbox
                                                onChange={
                                                    handleNoRestrictionsChange
                                                }
                                            >
                                                <span
                                                    style={{
                                                        color: "#101828",
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    No restrictions
                                                </span>
                                            </Checkbox>
                                        </FormItem>
                                        <div
                                            style={{
                                                color: "#667085",
                                                marginLeft: 24,
                                            }}
                                        >
                                            All users can see and interact with
                                            tasks in this status.
                                        </div>
                                    </div>
                                ),
                                styles: {
                                    body: {
                                        border: "1px solid #d0d5dd",
                                        borderRadius: "0 0 12px 12px",
                                        borderTop: "none",
                                        padding: "16px 18px 18px",
                                    },
                                    header: {
                                        alignItems: "center",
                                        border: "1px solid #d0d5dd",
                                        borderRadius:
                                            activeCollapseKey.length > 0
                                                ? "12px 12px 0 0"
                                                : "12px",
                                        minHeight: 72,
                                        padding: "16px 18px",
                                    },
                                },
                            },
                        ]}
                    />
                </div>
            </Form>
        </AppModal>
    );
};

export default observer(TaskStatusEditDialog);
